// The switch on a data widget's own header: the period it shows, and whether
// it draws as a number, a line or bars (or a list or a table). A source that
// can be asked for a period (Google's reads) is read again for the new one, and
// the widget says so while it reads; any other number shows its own points for
// the period at once. A widget with nothing to choose shows no switch.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { create } from "zustand";
import { BarChart3, ChevronDown, Hash, List, PieChart, Table2, TrendingUp } from "lucide-react";
import { SPEC } from "../../shared/catalog";
import { historyChoices, periodName, periodSource, VIEW_NAMES, viewOf, viewPatch, viewsFor, widgetPeriod, type DataView, type PeriodSource } from "../../shared/period";
import type { Pull, Stream, WidgetItem } from "../../shared/types";
import { api } from "../lib/api";
import { cx } from "../lib/util";
import { commit } from "../state/actions";
import { get, set, toast, useStore } from "../state/store";

/** The periods being read now, by widget: the switch spins and the widget dims its last data until the read is in. */
export const usePending = create<{ days: Record<string, number> }>(() => ({ days: {} }));

export interface PeriodState {
  /** the period the widget shows, in days (0 for all of it) */
  days: number;
  /** the periods it can show; none when there is nothing to choose */
  choices: readonly number[];
  /** the source that reads each period, when one does */
  source: PeriodSource | null;
  /** a period being read now */
  pending: number | null;
}

/** What a data widget's period is and can be, from its source or its own points. */
export function periodState(item: WidgetItem, stream: Stream | null, pull: Pull | null, pending: number | null = null): PeriodState {
  const source = periodSource(pull);
  const own = !source && stream && stream.shape === "number" ? historyChoices(stream.points) : [];
  // where the board cannot see the source (a shared view), the period its data says it covers
  const days = !source && stream?.period && item.props.period === undefined ? stream.period.days : widgetPeriod(item, source);
  // a period it was given before that the list does not have (an old chart's 30 days) stays chosen
  const choices = source ? source.choices : own.length && !own.includes(days) ? [...own, days].sort((a, b) => (a || 1e9) - (b || 1e9)) : own;
  return { days, choices, source, pending };
}

export function usePeriod(item: WidgetItem, stream: Stream | null): PeriodState {
  const pull = useStore((s) => (stream?.pullId ? (s.pulls[stream.pullId] ?? null) : null));
  const pending = usePending((s) => s.days[item.id] ?? null);
  return periodState(item, stream, pull, pending);
}

/** Show a widget for another period: its source reads it, or its own points show it at once. */
export async function choosePeriod(item: WidgetItem, days: number, state: PeriodState) {
  if (days === state.days && !state.pending) return;
  if (!state.source) {
    const it = get().items[item.id];
    if (it?.kind === "widget") commit([{ op: "item.put", item: { ...it, props: { ...it.props, period: days } } }], "Change the period");
    return;
  }
  usePending.setState((s) => ({ days: { ...s.days, [item.id]: days } }));
  try {
    const r = await api<{ item: WidgetItem; pull: Pull | null; stream: Stream | null }>(`/api/items/${encodeURIComponent(item.id)}/period`, { body: { days } });
    set((s) => ({
      items: { ...s.items, [r.item.id]: r.item },
      pulls: r.pull ? { ...s.pulls, [r.pull.id]: r.pull } : s.pulls,
      streams: r.stream ? { ...s.streams, [r.stream.key]: r.stream } : s.streams,
    }));
    if (r.pull?.status === "error") toast({ level: "warn", title: `Could not read ${periodName(days).toLowerCase()} yet`, body: r.pull.error });
  } catch (e) {
    toast({ level: "error", title: `Could not change the period`, body: (e as Error).message });
  } finally {
    usePending.setState((s) => {
      const next = { ...s.days };
      delete next[item.id];
      return { days: next };
    });
  }
}

/** Draw a widget another way, in its place, with the same data; a tile smaller than the new kind's least grows to it. */
export function chooseView(item: WidgetItem, view: DataView) {
  if (viewOf(item) === view) return;
  const it = get().items[item.id];
  if (!it || it.kind !== "widget") return;
  const next = viewPatch(it, view);
  const [mw, mh] = SPEC[next.type].min;
  commit([{ op: "item.put", item: { ...it, type: next.type, props: next.props, w: Math.max(it.w, mw), h: Math.max(it.h, mh) } }], `Show as ${VIEW_NAMES[view].toLowerCase()}`);
}

const VIEW_ICON: Record<DataView, typeof Hash> = { number: Hash, line: TrendingUp, bar: BarChart3, split: PieChart, list: List, table: Table2 };

/** The header's switch: "28d" and how it draws, opening the choices beside it. Read-only, the period alone, as words. */
export function DataSwitch({ item, stream, readOnly }: { item: WidgetItem; stream: Stream | null; readOnly: boolean }) {
  const state = usePeriod(item, stream);
  const view = viewOf(item);
  const views = viewsFor(stream, item);
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement | null>(null);
  const hasData = !!stream && stream.updatedAt > 0;
  const periods = hasData && (item.type === "metric" || item.type === "chart" || !!state.source) ? state.choices : [];
  // a map of a Google read has a period and no other way to draw
  if (!periods.length && (!view || views.length < 2)) return null;
  const Icon = view ? VIEW_ICON[view] : null;
  const shown = state.pending ?? state.days;
  if (readOnly) return periods.length ? <span className="tile-period is-static">{periodName(shown, true)}</span> : null;
  return (
    <>
      <button
        ref={btn}
        className={cx("tile-period", open && "is-open", state.pending !== null && "is-reading")}
        aria-label={`${periods.length ? periodName(shown) : ""}${view && views.length > 1 ? `${periods.length ? ", " : ""}shown as ${VIEW_NAMES[view].toLowerCase()}` : ""}. Change`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={state.pending !== null ? `Reading ${periodName(state.pending).toLowerCase()}` : "Period and chart"}
        onPointerDown={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {Icon && views.length > 1 && <Icon size={12} strokeWidth={2.2} />}
        {periods.length > 0 && <span>{periodName(shown, true)}</span>}
        {state.pending !== null ? <span className="spinner" /> : <ChevronDown size={12} strokeWidth={2.2} />}
      </button>
      {open && <SwitchMenu anchor={btn.current} item={item} state={state} periods={periods} views={views} view={view} onClose={() => setOpen(false)} />}
    </>
  );
}

function SwitchMenu({ anchor, item, state, periods, views, view, onClose }: { anchor: HTMLElement | null; item: WidgetItem; state: PeriodState; periods: readonly number[]; views: DataView[]; view: DataView | null; onClose: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const a = anchor.getBoundingClientRect();
    const m = ref.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(window.innerWidth - m.width - 8, a.right - m.width));
    const below = a.bottom + 6;
    setAt({ left, top: below + m.height > window.innerHeight - 8 ? Math.max(8, a.top - m.height - 6) : below });
  }, [anchor]);
  useEffect(() => {
    const away = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor?.contains(t)) onClose();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("pointerdown", away, true);
    document.addEventListener("keydown", key, true);
    return () => {
      document.removeEventListener("pointerdown", away, true);
      document.removeEventListener("keydown", key, true);
    };
  }, [anchor, onClose]);
  const shown = state.pending ?? state.days;
  return createPortal(
    <div
      ref={ref}
      className="island menu data-switch"
      role="menu"
      style={at ? { left: at.left, top: at.top } : { left: -9999, top: 0 }}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
    >
      {periods.length > 0 && (
        <>
          <div className="ds-label">Period</div>
          <div className="ds-periods">
            {periods.map((d) => (
              <button
                key={d}
                role="menuitemradio"
                aria-checked={d === shown}
                className={cx("ds-period", d === shown && "is-on")}
                title={periodName(d)}
                onClick={() => {
                  void choosePeriod(item, d, state);
                  onClose();
                }}
              >
                {periodName(d, true)}
              </button>
            ))}
          </div>
        </>
      )}
      {view && views.length > 1 && (
        <>
          <div className="ds-label">Show as</div>
          <div className="ds-views">
            {views.map((v) => {
              const I = VIEW_ICON[v];
              return (
                <button
                  key={v}
                  role="menuitemradio"
                  aria-checked={v === view}
                  className={cx("ds-view", v === view && "is-on")}
                  onClick={() => {
                    chooseView(item, v);
                    onClose();
                  }}
                >
                  <I size={15} strokeWidth={2} />
                  <span>{VIEW_NAMES[v]}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
      <button
        className="ds-more"
        onClick={() => {
          set({ selection: [item.id], inspect: true, editing: null, panel: null });
          onClose();
        }}
      >
        Compare, colors and more
      </button>
    </div>,
    // inside the board's root, where its theme's colors are
    document.querySelector(".board-root") ?? document.body,
  );
}


